/**
 * usePreviewCountdown.ts
 *
 * Derives hours and minutes remaining from premiumPreviewExpiresAt.
 * Updates every 60 seconds — no per-second ticking.
 * Returns null when preview is inactive, expired, or expiresAt is missing/invalid.
 *
 * When the countdown reaches zero mid-session, triggers a single subscription
 * refetch so AppContext updates entitlement without requiring a page reload.
 *
 * Target: src/hooks/usePreviewCountdown.ts
 */

import { useState, useEffect, useRef } from 'react';

export interface PreviewCountdownResult {
  hours: number;
  minutes: number;
}

function computeRemaining(expiresAt: string): PreviewCountdownResult | null {
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return null;
  const remainingMs = expiresMs - Date.now();
  if (remainingMs <= 0) return null;
  const totalMinutes = Math.floor(remainingMs / 60_000);
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

export function usePreviewCountdown(
  previewActive: boolean,
  premiumPreviewExpiresAt: string | null,
  refetchSubscription?: () => Promise<void>,
): PreviewCountdownResult | null {
  const [remaining, setRemaining] = useState<PreviewCountdownResult | null>(() => {
    if (!previewActive || !premiumPreviewExpiresAt) return null;
    return computeRemaining(premiumPreviewExpiresAt);
  });

  const hasTriggeredRefetchRef = useRef(false);

  useEffect(() => {
    if (!previewActive || !premiumPreviewExpiresAt) {
      setRemaining(null);
      return;
    }

    const update = () => {
      const result = computeRemaining(premiumPreviewExpiresAt);
      setRemaining(result);

      // Expiry detection: fire once when countdown reaches zero mid-session
      if (result === null && previewActive && !hasTriggeredRefetchRef.current) {
        hasTriggeredRefetchRef.current = true;
        refetchSubscription?.();
      }
    };

    update();

    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [previewActive, premiumPreviewExpiresAt, refetchSubscription]);

  return remaining;
}
